import React, { Component, memo } from 'react';

import cn from 'clsx';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import { Editor } from 'react-draft-wysiwyg';

import { Box } from '@mui/material';

import { LargeHeaderIcon } from '~/components/Icons/LargeHeaderIcon';
import { SmallHeaderIcon } from '~/components/Icons/SmallHeaderIcon';

const localization = {
  locale: 'en',
  translations: {
    'components.controls.blocktype.h3': (
      <span title="Large Header">
        <LargeHeaderIcon />
      </span>
    ),
    'components.controls.blocktype.h4': (
      <span title="Small Header">
        <SmallHeaderIcon />
      </span>
    ),
  },
};

const toolbar = {
  options: ['inline', 'blockType', 'list', 'link', 'history'],
  inline: {
    options: ['bold'],
  },
  blockType: {
    inDropdown: false,
    options: ['H3', 'H4'],
  },
  list: {
    options: ['ordered', 'unordered'],
  },
  history: {
    options: ['undo', 'redo'],
    title: 'History',
  },
};

const EDITOR_STYLE = {
  padding: '0 12px',
};

// There's nothing we can do about the markdown editor component
// re-rendering when it recieves many different props
// But we can prevent the editor itself from re-rendering
// unnecessarily by memo'ing it specifically with the props only it cares about
const EditorWrapper = memo((props) => {
  const { onBlur, readOnly, wrapperId, onChange, editorState } = props;

  return (
    <Editor
      onEditorStateChange={onChange}
      onBlur={onBlur}
      readOnly={readOnly}
      editorState={editorState}
      toolbar={toolbar}
      toolbarHidden={readOnly}
      wrapperId={wrapperId}
      localization={localization}
      editorStyle={EDITOR_STYLE}
    />
  );
});
EditorWrapper.displayName = 'EditorWrapper';

class TourMarkdownEditor extends Component {
  constructor(props) {
    super(props);
    const defaultValue = props.value || '';
    let contentState, newEditorState;
    if (defaultValue !== '') {
      contentState = markdownToDraft(defaultValue);
      newEditorState = EditorState.createWithContent(convertFromRaw(contentState));
    } else {
      newEditorState = EditorState.createEmpty();
    }

    this.state = {
      editorState: newEditorState,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.value !== prevProps.value) {
      const contentState = markdownToDraft(this.props.value);
      const newEditorState = EditorState.createWithContent(convertFromRaw(contentState));
      this.setState({ editorState: newEditorState });
    }
  }

  onEditorStateChange = (editorState) => {
    this.setState({ editorState });
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    let markdown = draftToMarkdown(rawContentState);

    if (this.props.onChange) {
      // this handles a current bug in stateToMarkdown
      // where an empty editor is saved with an empty 2 char return value
      // side effect user will not be able to save field with only one character
      // https://github.com/sstur/draft-js-utils/issues/131
      if (markdown.length === 2) {
        markdown = '';
      }
      this.props.onChange(markdown);
    }
  };

  onBlur = (e, editorState) => {
    if (this.props.onBlur) {
      const rawContentState = convertToRaw(editorState.getCurrentContent());
      const markdown = draftToMarkdown(rawContentState);
      this.props.onBlur(markdown);
    }
  };

  render() {
    return (
      <div>
        {!!this.props.title && <label>{this.props.title}</label>}
        {this.props.showLabel && !!this.props.label && <label>{this.props.label}</label>}
        <Box
          sx={{
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            '&.is-readonly': {
              backgroundColor: '#eeeeee',
            },
          }}
          className={cn({
            'is-readonly': this.props.readonly || this.props.disabled,
          })}
        >
          <EditorWrapper
            onBlur={this.onBlur}
            onChange={this.onEditorStateChange}
            editorState={this.state.editorState}
            readOnly={this.props.readonly || this.props.disabled}
            wrapperId={this.props.wrapperId}
          />
        </Box>
      </div>
    );
  }
}

export default TourMarkdownEditor;
